import apiClient from './api';
import config from '../../config/environment';

class AuthService {
  constructor() {
    this.ACCESS_TOKEN_EXPIRY = 3600; // 1 hour in seconds
    this.REFRESH_TOKEN_EXPIRY = 604800; // 7 days in seconds
  }

  async login(username, password) {
    try {
      const response = await apiClient.post(config.AUTH_ENDPOINTS.LOGIN, {
        username,
        password,
      });

      const { accessToken, refreshToken } = response.data;
      
      // Store tokens in localStorage
      localStorage.setItem(config.STORAGE_KEYS.ACCESS_TOKEN, accessToken);
      localStorage.setItem(config.STORAGE_KEYS.REFRESH_TOKEN, refreshToken);
      
      return response.data;
    } catch (error) {
      console.error('Login error:', error);
      throw error;
    }
  }

  async register(username, password) {
    try {
      const response = await apiClient.post(config.AUTH_ENDPOINTS.REGISTER, {
        username,
        password,
      });

      return response.data;
    } catch (error) {
      console.error('Registration error:', error);
      throw error;
    }
  }

  async logout() {
    try {
      const refreshToken = this.getRefreshToken();
      console.log('Logout - refreshToken:', refreshToken ? 'exists' : 'not found');
      console.log('Logout endpoint:', config.AUTH_ENDPOINTS.LOGOUT);
      
      if (refreshToken) {
        // Call logout endpoint to invalidate token on server
        const response = await apiClient.post(config.AUTH_ENDPOINTS.LOGOUT, {
          refreshToken,
        });
        console.log('Logout response:', response);
      }
    } catch (error) {
      console.error('Logout error:', error);
      // Continue with local cleanup even if server call fails
    } finally {
      this.clearTokens();
      // Redirect to login page
      window.location.href = '/login';
    }
  }

  isAuthenticated() {
    const accessToken = localStorage.getItem(config.STORAGE_KEYS.ACCESS_TOKEN);
    if (!accessToken) {
      return false;
    }
    
    // Check if token is expired
    try {
      const tokenData = this.parseJwt(accessToken);
      const currentTime = Math.floor(Date.now() / 1000);
      
      if (tokenData.exp && tokenData.exp < currentTime) {
        // Token is expired, clear tokens
        this.clearTokens();
        return false;
      }
      
      return true;
    } catch (error) {
      console.error('Error parsing token:', error);
      this.clearTokens();
      return false;
    }
  }

  getAccessToken() {
    return localStorage.getItem(config.STORAGE_KEYS.ACCESS_TOKEN);
  }

  getRefreshToken() {
    return localStorage.getItem(config.STORAGE_KEYS.REFRESH_TOKEN);
  }

  // Parse JWT token to get payload
  parseJwt(token) {
    try {
      const base64Url = token.split('.')[1];
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      const jsonPayload = decodeURIComponent(atob(base64).split('').map(function(c) {
        return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
      }).join(''));
      return JSON.parse(jsonPayload);
    } catch (error) {
      console.error('Error parsing JWT:', error);
      return {};
    }
  }

  // Clear tokens from localStorage
  clearTokens() {
    localStorage.removeItem(config.STORAGE_KEYS.ACCESS_TOKEN);
    localStorage.removeItem(config.STORAGE_KEYS.REFRESH_TOKEN);
  }

  // Check if token will expire soon (within 5 minutes)
  isTokenExpiringSoon(token) {
    try {
      const tokenData = this.parseJwt(token);
      const currentTime = Math.floor(Date.now() / 1000);
      const fiveMinutes = 5 * 60; // 5 minutes in seconds
      
      return tokenData.exp && (tokenData.exp - currentTime) < fiveMinutes;
    } catch (error) {
      return false; // If we can't parse the token, assume it's not expiring
    }
  }

  async refreshAccessToken() {
    try {
      const refreshToken = this.getRefreshToken();
      console.log('Refresh - refreshToken:', refreshToken ? 'exists' : 'not found');
      console.log('Refresh endpoint:', config.AUTH_ENDPOINTS.REFRESH);
      
      if (!refreshToken) {
        throw new Error('No refresh token available');
      }

      const response = await apiClient.post(config.AUTH_ENDPOINTS.REFRESH, {
        refreshToken,
      });

      console.log('Refresh response:', response.data);
      const { accessToken } = response.data;
      localStorage.setItem(config.STORAGE_KEYS.ACCESS_TOKEN, accessToken);
      
      return accessToken;
    } catch (error) {
      console.error('Token refresh error:', error);
      await this.logout();
      throw error;
    }
  }
}

export default new AuthService(); 